import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Search } from "lucide-react";

function Toggle({ on, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap ${on ? "bg-brand-green/10 text-brand-green" : "bg-brand-red text-white"}`}>
      {on ? "🟢 Dostupno" : "🔴 Nedostupno"}
    </button>
  );
}

export default function AvailabilityPage() {
  const { restaurant } = useOutletContext();
  const rid = restaurant.id;
  const [data, setData] = useState({ categories: [], products: [], option_groups: [] });
  const [q, setQ] = useState("");
  const load = async () => { try { const r = await api.get(`/restaurant/availability?restaurant_id=${rid}`); setData(r.data); } catch { toast.error("Greška pri učitavanju"); } };
  useEffect(() => { load(); }, [rid]);

  const toggleProduct = async (p) => {
    const nv = !p.available;
    setData((d) => ({ ...d, products: d.products.map((x) => x.id === p.id ? { ...x, available: nv } : x) }));
    try { await api.patch(`/restaurant/products/${p.id}/available`, { available: nv }); toast.success(`${p.name}: ${nv ? "dostupno" : "nedostupno"}`); }
    catch { toast.error("Greška"); load(); }
  };
  const toggleItem = async (gid, it) => {
    const nv = !it.active;
    setData((d) => ({ ...d, option_groups: d.option_groups.map((g) => g.id === gid ? { ...g, items: g.items.map((i) => i.id === it.id ? { ...i, active: nv } : i) } : g) }));
    try { await api.patch(`/restaurant/option-items/${gid}/${it.id}/available`, { active: nv }); }
    catch { toast.error("Greška"); load(); }
  };
  const toggleGroup = async (g) => {
    const nv = !g.active;
    setData((d) => ({ ...d, option_groups: d.option_groups.map((x) => x.id === g.id ? { ...x, active: nv } : x) }));
    try { await api.patch(`/restaurant/option-groups/${g.id}/available`, { active: nv }); }
    catch { toast.error("Greška"); load(); }
  };

  const ql = q.trim().toLowerCase();
  const catName = useMemo(() => Object.fromEntries(data.categories.map((c) => [c.id, c.name])), [data.categories]);
  const prodsByCat = useMemo(() => {
    const filtered = data.products.filter((p) => !ql || p.name.toLowerCase().includes(ql));
    const by = {};
    for (const p of filtered) (by[p.category_id] = by[p.category_id] || []).push(p);
    return by;
  }, [data.products, ql]);
  const shownGroups = data.option_groups
    .map((g) => ({ ...g, items: g.items.filter((i) => !ql || i.name.toLowerCase().includes(ql) || g.name.toLowerCase().includes(ql)) }))
    .filter((g) => !ql || g.items.length > 0 || g.name.toLowerCase().includes(ql));

  return (
    <div className="max-w-3xl" data-testid="availability-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-black text-2xl">Dostupnost</h1>
      </div>
      <div className="relative mb-5">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="avail-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pretraži proizvod ili opciju (npr. sos)"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-slate-200 outline-none focus:ring-2 focus:ring-brand-red" />
      </div>

      {data.categories.map((c) => (prodsByCat[c.id] || []).length > 0 && (
        <div key={c.id} className="mb-5">
          <h2 className="font-display font-black text-sm uppercase tracking-wide text-slate-500 mb-2">{c.name}</h2>
          <div className="bg-white rounded-xl border overflow-hidden">
            {prodsByCat[c.id].map((p) => (
              <div key={p.id} data-testid={`avail-prod-${p.id}`} className="flex items-center justify-between gap-3 p-3 border-b last:border-0">
                <span className={`font-semibold ${p.available ? "" : "text-slate-400 line-through"}`}>{p.name}</span>
                <Toggle on={p.available} onClick={() => toggleProduct(p)} testid={`avail-prod-toggle-${p.id}`} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {shownGroups.length > 0 && (
        <div className="mb-5">
          <h2 className="font-display font-black text-sm uppercase tracking-wide text-slate-500 mb-2">Opcije / Dodaci</h2>
          {shownGroups.map((g) => (
            <div key={g.id} data-testid={`avail-group-${g.id}`} className="bg-white rounded-xl border overflow-hidden mb-3">
              <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 border-b">
                <span className="font-display font-bold">{g.name}</span>
                <Toggle on={g.active} onClick={() => toggleGroup(g)} testid={`avail-group-toggle-${g.id}`} />
              </div>
              {g.items.map((it) => (
                <div key={it.id} className={`flex items-center justify-between gap-3 p-3 pl-5 border-b last:border-0 ${g.active ? "" : "opacity-40"}`}>
                  <span className={`text-sm ${it.active ? "" : "text-slate-400 line-through"}`}>{it.name}</span>
                  <Toggle on={it.active} onClick={() => toggleItem(g.id, it)} testid={`avail-item-toggle-${it.id}`} />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
